/**
 * Permission Configuration — Sentinel
 * Mirrors Global's pattern exactly
 */

export type AccessLevel = 'off' | 'view' | 'contribute' | 'manage';

export interface PermissionGroup {
  key: string;
  label: string;
  icon: string;
}

export interface PermissionFeature {
  label: string;
  group: string;
  keyActions: string[];
  actions: Record<string, string>;
}

/**
 * PermissionUser — accepts Sentinel's User type.
 * permissions is a flat map: { [feature]: { level, scope } }
 */
export interface PermissionUser {
  permissions?: Record<string, { level?: string; scope?: string }>;
}

// Groups — matches side menu order exactly
export const PERMISSION_GROUPS: PermissionGroup[] = [
  { key: 'operations', label: 'Operations', icon: '⚡' },
  { key: 'finance', label: 'Accounting', icon: '💰' },
  { key: 'crm', label: 'CRM', icon: '👥' },
  { key: 'rewards', label: 'Rewards', icon: '🎁' },
  { key: 'peaceOfSleep', label: 'Peace of Sleep', icon: '🛏️' },
  { key: 'email', label: 'Email', icon: '📧' },
  { key: 'trainingHub', label: 'Training Academy', icon: '🎓' },
  { key: 'reports', label: 'Reports', icon: '📊' },
  { key: 'admin', label: 'Administration', icon: '⚙️' },
  { key: 'superAdmin', label: 'Super Admin', icon: '🔒' },
  { key: 'resources', label: 'Resources', icon: '📚' },
  { key: 'marketing', label: 'Marketing Hub', icon: '📣' },
];

// Features — matches navigation paths
// Simplified: levels only (off/view/contribute/manage), no action checkboxes
export const PERMISSION_FEATURES: Record<string, PermissionFeature> = {
  // Operations
  dashboard:        { label: 'Dashboard',        group: 'operations', keyActions: [], actions: { level: 'Access Level' } },
  bookings:         { label: 'Bookings',         group: 'operations', keyActions: [], actions: { level: 'Access Level' } },
  service_logs:     { label: 'Service Logs',     group: 'operations', keyActions: [], actions: { level: 'Access Level' } },
  proposals:        { label: 'Proposals',           group: 'operations', keyActions: [], actions: { level: 'Access Level' } },
  certification:    { label: 'Service Certificates', group: 'operations', keyActions: ['verify', 'authorize'], actions: { level: 'Access Level', verify: 'Verify', authorize: 'Authorize' } },
  // Finance
  invoices:         { label: 'Invoices',         group: 'finance',    keyActions: [], actions: { level: 'Access Level' } },
  items:            { label: 'Items',            group: 'finance',    keyActions: [], actions: { level: 'Access Level' } },
  payouts:          { label: 'Payouts',           group: 'finance',    keyActions: [], actions: { level: 'Access Level' } },
  // CRM
  leads:            { label: 'Leads',            group: 'crm',        keyActions: ['assign', 'convert'], actions: { level: 'Access Level', assign: 'Assign', convert: 'Convert' } },
  customers:        { label: 'Customers',        group: 'crm',        keyActions: [], actions: { level: 'Access Level' } },
  candidates:       { label: 'Candidates',       group: 'crm',        keyActions: ['assign'], actions: { level: 'Access Level', assign: 'Assign' } },
  vendors:          { label: 'Vendors',          group: 'crm',        keyActions: [], actions: { level: 'Access Level' } },
  // Rewards
  rewards_dashboard:  { label: 'Dashboard',        group: 'rewards',  keyActions: [], actions: { level: 'Access Level' } },
  referrals:          { label: 'Referrals',        group: 'rewards',  keyActions: [], actions: { level: 'Access Level' } },
  vouchers:           { label: 'Vouchers',         group: 'rewards',  keyActions: [], actions: { level: 'Access Level' } },
  agent_commissions:  { label: 'Agent Commissions', group: 'rewards', keyActions: [], actions: { level: 'Access Level' } },
  referral_credits:   { label: 'Credit Balances',  group: 'rewards',  keyActions: [], actions: { level: 'Access Level' } },
  rewards_settlements:{ label: 'Partner Invoicing', group: 'rewards', keyActions: [], actions: { level: 'Access Level' } },
  // Email
  customer_email:   { label: 'Customer Emails',   group: 'email',      keyActions: [], actions: { level: 'Access Level' } },
  lead_email:       { label: 'Lead Emails',        group: 'email',      keyActions: [], actions: { level: 'Access Level' } },
  candidate_email:  { label: 'Candidate Emails',   group: 'email',      keyActions: [], actions: { level: 'Access Level' } },
  system_emails:    { label: 'System Emails',     group: 'email',      keyActions: [], actions: { level: 'Access Level' } },
  // Training Academy
  training_trainees:     { label: 'Trainees',       group: 'trainingHub', keyActions: [], actions: { level: 'Access Level' } },
  training_courses:      { label: 'Courses',         group: 'trainingHub', keyActions: [], actions: { level: 'Access Level' } },
  training_modules:      { label: 'Modules',        group: 'trainingHub', keyActions: [], actions: { level: 'Access Level' } },
  training_assessments:  { label: 'Assessments',    group: 'trainingHub', keyActions: [], actions: { level: 'Access Level' } },
  training_content:      { label: 'Content',        group: 'trainingHub', keyActions: [], actions: { level: 'Access Level' } },
  // Reports
  transaction_reports:   { label: 'Service Transactions',  group: 'reports', keyActions: ['export'], actions: { level: 'Access Level', export: 'Export' } },
  sales_performance:     { label: 'Sales Performance',     group: 'reports', keyActions: [], actions: { level: 'Access Level' } },
  // Resources
  content_library:  { label: 'Content Library',  group: 'resources',  keyActions: [], actions: { level: 'Access Level' } },
  // Peace of Sleep
  pos_dashboard:          { label: 'Dashboard',          group: 'peaceOfSleep', keyActions: [], actions: { level: 'Access Level' } },
  pos_properties:         { label: 'Portfolio',          group: 'peaceOfSleep', keyActions: [], actions: { level: 'Access Level' } },
  pos_services:           { label: 'Services',           group: 'peaceOfSleep', keyActions: [], actions: { level: 'Access Level' } },
  pos_reports:            { label: 'Certificates',       group: 'peaceOfSleep', keyActions: [], actions: { level: 'Access Level' } },
  pos_ambassadors:        { label: 'Ambassadors',        group: 'peaceOfSleep', keyActions: [], actions: { level: 'Access Level' } },
  pos_charter_suite_users: { label: 'Charters', group: 'peaceOfSleep', keyActions: [], actions: { level: 'Access Level' } },
  pos_proposals:          { label: 'Proposals',          group: 'peaceOfSleep', keyActions: [], actions: { level: 'Access Level' } },
  hospitality:            { label: 'Hospitality', group: 'peaceOfSleep', keyActions: ['book'], actions: { level: 'Access Level', book: 'Book' } },
  // Administration
  users:            { label: 'Users',            group: 'admin',      keyActions: [], actions: { level: 'Access Level' } },
  territories:      { label: 'Territories',      group: 'admin',      keyActions: [], actions: { level: 'Access Level' } },
  equipment:        { label: 'Equipment',        group: 'admin',      keyActions: [], actions: { level: 'Access Level' } },
  split_codes:      { label: 'Split Codes',      group: 'admin',      keyActions: [], actions: { level: 'Access Level' } },
  contracts:        { label: 'Contracts',        group: 'crm',        keyActions: [], actions: { level: 'Access Level' } },
  system_comms:     { label: 'System Comms',     group: 'admin',      keyActions: [], actions: { level: 'Access Level' } },
  booking_config:   { label: 'Booking Settings', group: 'admin',      keyActions: [], actions: { level: 'Access Level' } },
  licensing_regions: { label: 'Regions',          group: 'admin',      keyActions: [], actions: { level: 'Access Level' } },
  // Super Admin
  permissions:      { label: 'Permission Profiles', group: 'superAdmin', keyActions: [], actions: { level: 'Access Level' } },
  platform_config:  { label: 'Platform Config',  group: 'superAdmin', keyActions: [], actions: { level: 'Access Level' } },
  territory_purchases: { label: 'Territory Purchases', group: 'superAdmin', keyActions: [], actions: { level: 'Access Level' } },
  system_revenue:   { label: 'System Revenue',   group: 'superAdmin', keyActions: [], actions: { level: 'Access Level' } },
  // Marketing Hub (31 Oct 2026, for Sam our agency partner): contribute writes posts and submits them for
  // approval; manage schedules, posts and approves, as head office does.
  social_posts:     { label: 'Social posts',     group: 'marketing',  keyActions: [], actions: { level: 'Access Level' } },
  // Comments and Reviews in CMB Chats and the Social inbox (3 Nov 2026): view reads; contribute or manage answers.
  social_inbox:     { label: 'Comments and reviews', group: 'marketing', keyActions: [], actions: { level: 'Access Level' } },
};

// Path to feature mapping
const PATH_TO_FEATURE: Record<string, string> = {
  '/': 'dashboard',
  '/bookings': 'bookings',
  '/service-logs': 'service_logs',
  '/invoices': 'invoices',
  '/items': 'items',
  '/vendors': 'vendors',
  '/payouts': 'payouts',
  '/system-revenue': 'system_revenue',
  '/reports/payout-history': 'payouts',
  '/reports/vendor-statements': 'payouts',
  '/reports/invoice-splits': 'payouts',
  '/reports/equipment-register': 'equipment',
  '/customers': 'customers',
  '/users': 'users',
  '/territories': 'territories',
  '/splits': 'split_codes',
  '/contracts': 'contracts',
  '/rewards': 'rewards_dashboard',
  '/rewards/codes': 'referrals',
  '/rewards/redemptions': 'referrals',
  '/rewards/vouchers': 'vouchers',
  '/rewards/credits': 'referral_credits',
  '/rewards/settlements': 'rewards_settlements',
  '/rewards/pool-ledger': 'rewards_dashboard',
  '/rewards/program-rules': 'rewards_dashboard',
  '/email/system': 'system_emails',
  '/system-comms': 'system_comms',
  '/config': 'platform_config',
  '/content-library': 'content_library',
  '/booking-settings': 'booking_config',
  '/equipment': 'equipment',
  '/regions': 'licensing_regions',
  '/permission-profiles': 'permissions',
  // Peace of Sleep
  '/pos-properties': 'pos_properties',
  '/pos-ambassadors': 'pos_ambassadors',
  '/charters': 'pos_charter_suite_users',
  '/pos-charter-suite-users': 'pos_charter_suite_users', // legacy alias
  '/pos-proposals': 'pos_proposals',
  '/hospitality': 'hospitality',
  // Leads + Estimator + Proposals
  '/leads': 'leads',
  '/candidates': 'candidates',
  '/estimator': 'proposals',
  '/proposals': 'proposals',
  // Training Academy
  '/training-trainees': 'training_trainees',
  '/training-courses': 'training_courses',
  '/training-modules': 'training_modules',
  '/training-assessments': 'training_assessments',
  '/training-content': 'training_content',
  // Certification
  '/certification': 'certification',
  '/certification/submit': 'certification',
  // Email
  '/email/customers': 'customer_email',
  '/email/leads': 'lead_email',
  '/email/candidates': 'candidate_email',
  // Reports
  '/reports/transactions': 'transaction_reports',
  '/reports/sales-performance': 'sales_performance',
  '/territory-purchases': 'territory_purchases',
  '/my-availability': 'bookings',
  '/marketing/social-posts': 'social_posts',
};

// Helper functions
const LEVEL_RANK: Record<AccessLevel, number> = {
  off: 0,
  view: 1,
  contribute: 2,
  manage: 3,
};

const ACTION_TO_LEVEL: Record<string, AccessLevel> = {
  view: 'view',
  list: 'view',
  create: 'contribute',
  edit: 'contribute',
  update: 'contribute',
  delete: 'manage',
  cancel: 'manage',
  manage: 'manage',
  process: 'manage',
};

/**
 * Check if user has at least the required access level for a feature.
 * NOTE: Admin/super-admin bypass should be done via appMetadata at the call site.
 */
export function hasAccess(
  user: PermissionUser,
  feature: string,
  requiredLevel: AccessLevel,
): boolean {
  if (!user?.permissions) return false;
  const featurePerms = user.permissions[feature];
  const userLevel = (featurePerms?.level as AccessLevel) || 'off';
  return (LEVEL_RANK[userLevel] || 0) >= (LEVEL_RANK[requiredLevel] || 0);
}

export function hasPermission(
  user: PermissionUser,
  feature: string,
  action: string,
): boolean {
  if (!user?.permissions) return false;
  const requiredLevel = ACTION_TO_LEVEL[action];
  if (requiredLevel) {
    const userLevel = (user.permissions[feature]?.level as AccessLevel) || 'off';
    return (LEVEL_RANK[userLevel] || 0) >= (LEVEL_RANK[requiredLevel] || 0);
  }
  return false;
}

export function hasFeatureAccess(
  user: PermissionUser,
  feature: string,
): boolean {
  if (!user?.permissions) return false;
  const featurePerms = user.permissions[feature];
  if (!featurePerms) return false;
  return !!featurePerms.level && featurePerms.level !== 'off';
}

/**
 * Check if a user can access a nav menu item by path.
 * Returns true if no feature mapping exists (allow by default).
 */
export function canAccessMenuItem(
  path: string,
  user: PermissionUser,
): boolean {
  if (!user?.permissions) return false;

  const feature = PATH_TO_FEATURE[path];
  if (!feature) return false;

  const featurePerms = user.permissions[feature];
  if (!featurePerms) return false;
  return !!featurePerms.level && featurePerms.level !== 'off';
}
